import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Authenticated, Unauthenticated } from "convex/react";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

export function LandingNavbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    if (!isHome) {
      navigate(`/#${sectionId}`);
      // Minimal timeout to allow navigation to complete before scrolling
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) element.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <nav
      className={cn(
        "fixed left-0 right-0 top-0 z-50 border-b transition-all duration-300",
        isScrolled
          ? "border-gray-200 bg-white/80 py-3 backdrop-blur-md"
          : "border-transparent bg-transparent py-5"
      )}
    >
      <div className="container mx-auto flex items-center justify-between px-4">
        {/* Left: Logo */}
        <div className="flex flex-1 items-center justify-start">
          <Link to="/" className="flex items-center">
            <img src="/logo.svg" alt="ManageClean OS" className="h-8" />
          </Link>
        </div>

        {/* Center: Nav links */}
        <div className="hidden flex-1 items-center justify-center gap-8 md:flex">
          <button
            onClick={() => scrollToSection("core")}
            className="text-sm font-medium text-slate-600 transition-colors hover:text-blue-600"
          >
            Intelligent Core
          </button>
          <button
            onClick={() => scrollToSection("features")}
            className="text-sm font-medium text-slate-600 transition-colors hover:text-blue-600"
          >
            Features
          </button>
          <Link
            to="/pricing"
            className="text-sm font-medium text-slate-600 transition-colors hover:text-blue-600"
          >
            Pricing
          </Link>
          <Link
            to="/success-hub"
            className="text-sm font-medium text-slate-600 transition-colors hover:text-blue-600"
          >
            Success Hub
          </Link>
        </div>

        {/* Right: Auth buttons / Mobile toggle */}
        <div className="flex flex-1 items-center justify-end gap-3">
          <div className="hidden items-center gap-3 md:flex">
            <Unauthenticated>
              <Link to="/sign-in">
                <Button variant="ghost" className="text-slate-600 hover:text-blue-600">
                  Sign In
                </Button>
              </Link>
              <Link to="/sign-up">
                <Button className="bg-slate-900 text-white hover:bg-slate-800">Get Started</Button>
              </Link>
            </Unauthenticated>
            <Authenticated>
              <Button
                onClick={() => navigate("/dashboard")}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                Dashboard
              </Button>
            </Authenticated>
          </div>
          
          <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-b border-gray-100 bg-white md:hidden"
          >
            <div className="flex flex-col gap-4 p-4">
              <button
                onClick={() => scrollToSection("core")}
                className="text-left text-sm font-medium text-slate-600"
              >
                Intelligent Core
              </button>
              <button
                onClick={() => scrollToSection("features")}
                className="text-left text-sm font-medium text-slate-600"
              >
                Features
              </button>
              <Link
                to="/pricing"
                className="text-left text-sm font-medium text-slate-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                Pricing
              </Link>
              <Link
                to="/success-hub"
                className="text-left text-sm font-medium text-slate-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                Success Hub
              </Link>
              <div className="my-2 h-px bg-gray-100" />
              <Unauthenticated>
                <Link to="/sign-in" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start">
                    Sign In
                  </Button>
                </Link>
                <Link to="/sign-up" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full">Get Started</Button>
                </Link>
              </Unauthenticated>
              <Authenticated>
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="w-full bg-blue-600 text-white hover:bg-blue-700"
                >
                  Dashboard
                </Button>
              </Authenticated>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
